/**
 * WebSocket实时推送服务
 * 用于语音通话状态、消息通知等实时推送
 */

import SockJS from 'sockjs-client'
import { Stomp } from '@stomp/stompjs'

export interface WebSocketCallbacks {
  onCallStatusChange?: (data: CallStatusMessage) => void
  onIncomingCall?: (data: CallInviteMessage) => void
  onNewMessage?: (data: MessageNotification) => void
  onSystemNotification?: (data: SystemNotification) => void
  onConnected?: () => void
  onDisconnected?: () => void
  onError?: (error: any) => void
}

export interface CallStatusMessage {
  type: string
  inviteId: number
  roomId: string
  status: number
  statusText: string
  callDuration?: number
  timestamp: number
  senderId?: number
}

export interface CallInviteMessage {
  type: string
  inviteId: number
  roomId: string
  chatId: number
  callerId: number
  callerName: string
  callerAvatar?: string
  expireTime?: number
  timestamp: number
}

export interface MessageNotification {
  type: string
  chatId: number
  senderName: string
  messageCount: number
  timestamp: number
}

export interface SystemNotification {
  type: string
  title: string
  message: string
  timestamp: number
}

export class WebSocketService {
  private client: any = null
  private connected: boolean = false
  private reconnectAttempts: number = 0
  private maxReconnectAttempts: number = 5
  private reconnectTimer: any = null
  private callbacks: WebSocketCallbacks = {}
  private apiBaseUrl: string = ''

  /**
   * 连接WebSocket
   */
  connect(token: string, callbacks: WebSocketCallbacks = {}) {
    console.log('🔌 [WebSocket] 开始连接...')
    console.log('🔌 [WebSocket] API URL:', `${this.apiBaseUrl}/ws`)
    console.log('🔌 [WebSocket] Token:', token ? '有' : '无')
    console.log('🔌 [WebSocket] 平台:', Capacitor.getPlatform())

    this.callbacks = callbacks
    this.apiBaseUrl = this.getApiBaseUrl()

    // 创建SockJS连接
    const socket = new SockJS(`${this.apiBaseUrl}/ws`)
    console.log('🔌 [WebSocket] SockJS对象已创建')

    // 创建STOMP客户端
    this.client = Stomp.over(socket)
    console.log('🔌 [WebSocket] STOMP客户端已创建')

    // 配置心跳
    this.client.heartbeat.outgoing = 20000
    this.client.heartbeat.incoming = 20000
    console.log('🔌 [WebSocket] 心跳配置完成')

    // 连接WebSocket
    this.client.connect(
      {
        token: token
      },
      (frame: any) => {
        console.log('✅ [WebSocket] 连接成功回调触发', frame)
        this.onConnected(frame)
      },
      (error: any) => {
        console.error('❌ [WebSocket] 连接失败回调触发', error)
        this.onError(error)
      }
    )
    console.log('🔌 [WebSocket] 连接请求已发送')
  }

  /**
   * 连接成功回调
   */
  private onConnected(frame: any) {
    console.log('✅ [WebSocket] 连接成功 - 详细信息:', frame)
    this.connected = true
    this.reconnectAttempts = 0
    console.log('✅ [WebSocket] 连接状态已设置为: 已连接')
    console.log('✅ [WebSocket] 重连次数已重置为: 0')

    // 订阅各个频道
    console.log('📡 [WebSocket] 开始订阅频道...')
    this.subscribeToCallStatus()
    this.subscribeToMessages()
    this.subscribeToChatInvites()
    this.subscribeToNotifications()
    console.log('📡 [WebSocket] 所有频道订阅完成')

    // 触发连接成功回调
    if (this.callbacks.onConnected) {
      console.log('✅ [WebSocket] 触发连接成功回调')
      this.callbacks.onConnected()
    } else {
      console.log('⚠️ [WebSocket] 没有设置连接成功回调')
    }
  }

  /**
   * 订阅通话状态变化
   */
  private subscribeToCallStatus() {
    console.log('📡 [WebSocket] 正在订阅通话状态频道: /user/queue/call-status')
    this.client.subscribe('/user/queue/call-status', (message: any) => {
      try {
        console.log('📡 [WebSocket] 收到通话状态消息 - 原始数据:', message)
        const data: CallStatusMessage = JSON.parse(message.body)
        console.log('📞 [WebSocket] 通话状态变化 - 解析后数据:', data)

        if (this.callbacks.onCallStatusChange) {
          console.log('✅ [WebSocket] 触发通话状态变化回调')
          this.callbacks.onCallStatusChange(data)
        } else {
          console.log('⚠️ [WebSocket] 没有设置通话状态变化回调')
        }
      } catch (error) {
        console.error('❌ [WebSocket] 解析通话状态消息失败:', error)
      }
    })
  }

  /**
   * 订阅新消息通知
   */
  private subscribeToMessages() {
    this.client.subscribe('/user/queue/messages', (message: any) => {
      try {
        const data: MessageNotification = JSON.parse(message.body)
        console.log('💬 新消息通知:', data)

        if (this.callbacks.onNewMessage) {
          this.callbacks.onNewMessage(data)
        }
      } catch (error) {
        console.error('❌ 解析新消息通知失败:', error)
      }
    })
  }

  /**
   * 订阅聊天室通话邀请
   */
  private subscribeToChatInvites() {
    this.client.subscribe('/topic/chat/*/calls', (message: any) => {
      try {
        const data: CallInviteMessage = JSON.parse(message.body)
        console.log('🔔 来电邀请:', data)

        if (this.callbacks.onIncomingCall) {
          this.callbacks.onIncomingCall(data)
        }
      } catch (error) {
        console.error('❌ 解析来电邀请失败:', error)
      }
    })
  }

  /**
   * 订阅系统通知
   */
  private subscribeToNotifications() {
    this.client.subscribe('/user/queue/notifications', (message: any) => {
      try {
        const data: SystemNotification = JSON.parse(message.body)
        console.log('📢 系统通知:', data)

        if (this.callbacks.onSystemNotification) {
          this.callbacks.onSystemNotification(data)
        }
      } catch (error) {
        console.error('❌ 解析系统通知失败:', error)
      }
    })
  }

  /**
   * 错误处理和重连
   */
  private onError(error: any) {
    console.error('❌ [WebSocket] 发生错误 - 详细信息:', error)
    console.error('❌ [WebSocket] 错误类型:', error?.name || '未知')
    console.error('❌ [WebSocket] 错误消息:', error?.message || '无消息')
    this.connected = false
    console.log('🔌 [WebSocket] 连接状态已设置为: 未连接')

    // 触发错误回调
    if (this.callbacks.onError) {
      this.callbacks.onError(error)
    }

    // 尝试重连
    this.handleReconnect()
  }

  /**
   * 处理重连
   */
  private handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      console.log(`🔄 尝试重连WebSocket... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`)

      this.reconnectTimer = setTimeout(() => {
        // 从localStorage重新获取token
        const token = localStorage.getItem('user_token')
        if (token) {
          this.connect(token, this.callbacks)
        }
      }, 3000)
    } else {
      console.error('❌ WebSocket重连失败，达到最大重连次数')
      if (this.callbacks.onDisconnected) {
        this.callbacks.onDisconnected()
      }
    }
  }

  /**
   * 断开连接
   */
  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    if (this.client && this.connected) {
      this.client.disconnect(() => {
        console.log('🔌 WebSocket已断开')
        this.connected = false
      })
    }
  }

  /**
   * 发送消息到服务器
   */
  send(destination: string, message: any) {
    if (this.client && this.connected) {
      this.client.send(destination, {}, JSON.stringify(message))
    } else {
      console.warn('⚠️ WebSocket未连接，无法发送消息')
    }
  }

  /**
   * 获取API基础URL
   */
  private getApiBaseUrl(): string {
    // 从环境变量或localStorage中获取API基础URL
    const envApiUrl = import.meta.env.VITE_API_BASE_URL
    if (envApiUrl) {
      return envApiUrl
    }

    // 从localStorage获取（ Capacitor 原生层设置）
    const localApiUrl = localStorage.getItem('api_base_url')
    if (localApiUrl) {
      return localApiUrl
    }

    // 默认使用当前域名
    return window.location.origin
  }

  /**
   * 检查连接状态
   */
  isConnected(): boolean {
    return this.connected
  }
}

// 导出单例
export const websocketService = new WebSocketService()